import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, ExternalLink, Play, Pause, Share2, Check, ScrollText } from "lucide-react";
import api from "../../lib/api";
import { plainLyrics } from "./Lyrics";
import { LyricsView } from "./LyricsView";

export const API = process.env.REACT_APP_BACKEND_URL + "/api";
export const platform = (url = "") => (/spotify/.test(url) ? "Spotify" : /youtu/.test(url) ? "YouTube" : /soundcloud/.test(url) ? "SoundCloud" : "Listen");
export const mmss = (s) => (s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : "");
export const coverUrl = (song) => (song.has_cover ? `${API}/music/${song.id}/cover` : null);

// Counts one play per mount; shows a poster-style cover or a typographic fallback.
export function useCountedPlay(song) {
  const counted = useRef(false);
  return () => { if (!counted.current) { counted.current = true; api.post(`/music/${song.id}/play`).catch(() => {}); } };
}

export function Cover({ song, className = "", compact = false }) {
  const src = coverUrl(song);
  return src ? (
    <img src={src} alt={`${song.title} cover art`} className={`object-cover ${className}`} data-testid={`song-cover-${song.id}`} />
  ) : (
    <div className={`overflow-hidden bg-[radial-gradient(ellipse_at_30%_20%,#7a1f2b_0%,#2a0b10_55%,#0a0608_100%)] flex items-end ${compact ? "p-1" : "p-4"} ${className}`} data-testid={`song-cover-fallback-${song.id}`}>
      {!compact && <span className="font-display text-[#e9c46a] text-2xl leading-none uppercase tracking-tight">{song.title}</span>}
    </div>
  );
}

export function ShareButton({ song, className = "" }) {
  const [done, setDone] = useState(false);
  const url = `${window.location.origin}${song.share_path || `/music/${song.slug || song.id}`}`;
  const share = async () => {
    const text = `${song.title} — ${song.artist} · VeerGi Music Lab`;
    if (navigator.share) { try { await navigator.share({ title: text, url }); return; } catch { /* user cancelled */ } }
    await navigator.clipboard?.writeText(url); setDone(true); setTimeout(() => setDone(false), 1800);
  };
  return (
    <button type="button" onClick={share} data-testid={`song-share-${song.id}`}
      className={`inline-flex items-center gap-2 rounded-full border border-[#f3e9d2]/25 text-sm font-semibold px-4 py-2 hover:border-[#e9c46a] hover:text-[#e9c46a] transition-colors ${className}`}>
      {done ? <Check size={14} /> : <Share2 size={14} />} {done ? "Link copied" : "Share"}
    </button>
  );
}

export function SongCard({ song, index, playing = false, active = false, onToggle }) {
  const toggle = () => onToggle?.(index);
  const [showLyrics, setShowLyrics] = useState(false);
  void useCountedPlay;
  return (
    <li id={song.slug || song.id} className={`group relative grid sm:grid-cols-[220px_minmax(0,1fr)] gap-0 overflow-hidden rounded-sm border border-[#f3e9d2]/10 bg-[#120a0c]/80 backdrop-blur-md shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)] transition-transform duration-300 hover:-translate-y-0.5 ${active ? "ring-1 ring-[#e9c46a]/70" : ""}`} data-testid={`song-${song.id}`} data-active={active}>
      <button type="button" onClick={() => setShowLyrics(true)} aria-label={`View ${song.title} lyrics`} className="relative block w-full text-left aspect-square sm:aspect-auto sm:h-full overflow-hidden cursor-pointer" data-testid={`song-open-${song.id}`}>
        <Cover song={song} className="w-full h-full min-h-[220px] transition-transform duration-700 group-hover:scale-[1.04]" />
        <span className="absolute top-3 left-3 font-display text-[#e9c46a] text-xs tracking-[0.3em]">TRACK {String(index + 1).padStart(2, "0")}</span>
        <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-[#0b0709]/70 backdrop-blur px-3 py-1 text-[11px] tracking-[0.15em] uppercase text-[#f3e9d2]/80 opacity-0 group-hover:opacity-100 transition-opacity"><ScrollText size={12} /> Lyrics</span>
      </button>
      <div className="p-6 sm:p-8 flex flex-col">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl md:text-3xl leading-[1.05] text-[#f3e9d2]" data-testid={`song-title-${song.id}`}>
              <Link to={`/music/${song.slug || song.id}`} className="hover:text-[#e9c46a] transition-colors">{song.title}</Link>
            </h2>
            <p className="mt-2 text-sm uppercase tracking-[0.2em] text-[#f3e9d2]/55" data-testid={`song-artist-${song.id}`}>{song.artist}{song.duration_sec ? ` · ${mmss(song.duration_sec)}` : ""}{song.album_title && <> · <Link to={`/music/album/${song.album_slug}`} className="hover:text-[#e9c46a]" data-testid={`song-album-link-${song.id}`}>{song.album_title}</Link></>}</p>
          </div>
          <button type="button" onClick={toggle} aria-label={playing ? "Pause" : "Play"} data-testid={`song-play-${song.id}`}
            className="shrink-0 h-14 w-14 rounded-full bg-[#e9c46a] text-[#120a0c] grid place-items-center hover:scale-105 transition-transform shadow-[0_0_40px_-5px_rgba(233,196,106,0.6)]">
            {playing ? <Pause size={22} /> : <Play size={22} className="ml-0.5" />}
          </button>
        </div>
        {song.lyrics && <p className="mt-5 text-sm leading-7 text-[#f3e9d2]/70 line-clamp-3 whitespace-pre-line italic" data-testid={`song-lyrics-excerpt-${song.id}`}>{plainLyrics(song.lyrics)}</p>}
        <div className="mt-auto pt-6 flex flex-wrap items-center gap-2">
          <a href={`${API}/music/${song.id}/download`} download data-testid={`song-download-${song.id}`}
            className="inline-flex items-center gap-2 rounded-full bg-[#f3e9d2] text-[#120a0c] text-sm font-bold px-4 py-2 hover:bg-[#e9c46a] transition-colors">
            <Download size={14} /> Download {song.ext?.toUpperCase()}
          </a>
          {song.external_url && (
            <a href={song.external_url} target="_blank" rel="noopener noreferrer" data-testid={`song-external-${song.id}`}
              className="inline-flex items-center gap-2 rounded-full border border-[#f3e9d2]/25 text-sm font-semibold px-4 py-2 hover:border-[#e9c46a] hover:text-[#e9c46a] transition-colors">
              <ExternalLink size={14} /> {platform(song.external_url)}
            </a>
          )}
          <ShareButton song={song} />
          <button type="button" onClick={() => setShowLyrics(true)} data-testid={`song-view-lyrics-${song.id}`}
            className="inline-flex items-center gap-2 rounded-full border border-[#f3e9d2]/25 text-sm font-semibold px-4 py-2 hover:border-[#e9c46a] hover:text-[#e9c46a] transition-colors">
            <ScrollText size={14} /> View Lyrics
          </button>
        </div>
        {song.copyright && <p className="mt-4 text-[11px] text-[#f3e9d2]/40" data-testid={`song-copyright-${song.id}`}>{song.copyright}</p>}
      </div>
      {showLyrics && <LyricsView song={song} onClose={() => setShowLyrics(false)} />}
    </li>
  );
}
